import React, { useState, useRef, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, GraduationCap, Briefcase, CheckCircle2, TrendingUp, Recycle, Award, UserPlus, X, Loader2, Heart, RefreshCw, Handshake, Calendar, MapPin, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { saveSubmission } from '../services/databaseService';
import { sendViaWeb3Forms } from '../services/web3formsService';
type ModalType = 'membre' | 'recruter' | 'formation' | null;
export function HomePage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [charterAccepted, setCharterAccepted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(formRef.current!);
    let submission: any = {};
    if (activeModal === 'membre') {
      submission = {
        type: 'membre' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        telephone: formData.get('telephone') as string || '',
        email: formData.get('email') as string || '',
        ville: formData.get('ville') as string || '',
        profession: formData.get('profession') as string || '',
        typeMembre: formData.get('typeMembre') as string || '',
        contribution: formData.get('contribution') as string || '',
        motivations: formData.get('motivations') as string || ''
      };
    } else if (activeModal === 'recruter') {
      submission = {
        type: 'recruter' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        telephone: formData.get('telephone') as string || '',
        email: formData.get('email') as string || '',
        ville: formData.get('ville') as string || '',
        quartier: formData.get('quartier') as string || '',
        typePersonnel: formData.get('typePersonnel') as string || '',
        typeContrat: formData.get('typeContrat') as string || '',
        dateDebut: formData.get('dateDebut') as string || '',
        description: formData.get('description') as string || ''
      };
    } else if (activeModal === 'formation') {
      submission = {
        type: 'formation' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        age: formData.get('age') as string || '',
        ville: formData.get('ville') as string || '',
        quartier: formData.get('quartier') as string || '',
        telephone: formData.get('telephone') as string || '',
        whatsapp: formData.get('whatsapp') as string || '',
        email: formData.get('email') as string || '',
        typeFormation: formData.get('typeFormation') as string || '',
        motivations: formData.get('motivations') as string || '',
        charteEngagement: formData.get('charteEngagement') as string || ''
      };
    }
    setTimeout(async () => {
      try {
        saveSubmission(submission);
        // Envoyer via Web3Forms
        const formData2 = new FormData(formRef.current!);
        const fields: Record<string, string> = {};
        formData2.forEach((value, key) => {
          if (typeof value === 'string') {
            fields[key] = value;
          }
        });
        const web3Result = await sendViaWeb3Forms({
          type: activeModal!,
          fields
        });
        console.log('📧 Web3Forms result:', web3Result);
        setIsSubmitting(false);
        setIsSuccess(true);
      } catch (error) {
        console.error('Error saving submission:', error);
        setIsSubmitting(false);
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    }, 500);
  };
  const closeModal = () => {
    setActiveModal(null);
    setIsSuccess(false);
    setCharterAccepted(false);
  };
  const inputClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all';
  const selectClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all bg-white';
  const labelClass = 'text-sm font-medium text-gray-700';
  const modalConfigs = {
    membre: {
      title: "Devenir Membre de l'ADCC",
      subtitle: "Rejoignez notre communauté engagée pour l'insertion professionnelle digne",
      successMessage: "Votre demande d'adhésion a été enregistrée. Notre équipe vous contactera prochainement pour finaliser votre inscription.",
      icon: UserPlus
    },
    recruter: {
      title: 'Recruter un Personnel Formé',
      subtitle: 'Décrivez vos besoins et nous vous mettrons en relation avec des profils qualifiés',
      successMessage: 'Demande reçue ! Nous vous proposerons des profils correspondant à vos critères.',
      icon: Briefcase
    },
    formation: {
      title: "S'enregistrer pour une session",
      subtitle: 'Inscrivez-vous à une session ADCC et démarrez votre parcours vers un emploi digne',
      successMessage: 'Votre inscription a bien été enregistrée ! Notre équipe vous contactera très prochainement pour confirmer votre place et vous communiquer les détails de la session.',
      icon: GraduationCap
    }
  };
  return <Layout data-id="element-1030" data-mp="1kxir5to6zzji">
      <SEO {...pageSEO.home} data-id="element-1031" data-mp="3hrwrf68vei34" />

      {/* Hero Section */}
      <section className="relative bg-[#2C3E50] text-white py-20 md:py-32 overflow-hidden" data-id="element-1032" data-mp="3htg8gz2cox5y">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/jfY9WFQQPLNmCKBmbcpuEW/Femmes_Menage_Emploi_3.png')] bg-cover bg-center opacity-30" data-id="element-1033" data-mp="3mrrjve8anfqa"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1034" data-mp="oy6m3nwsd94u">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1035" data-mp="12x66ukh6h3vq">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-1036" data-mp="xycvaxlgbmy7">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1037" data-mp="xxblsxk338il">
                Développer. Valoriser.{' '}
                <span className="text-[#1E88E5]" data-id="element-1038" data-mp="1ct9bbwzi5elu">Insérer.</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-6 leading-relaxed" data-id="element-1039" data-mp="17vr9eqjwjznc">
                L'Association Dignité, Compétences Création (ADCC) agit au Bénin
                pour l'accompagnement à la professionnalisation et l'insertion
                de personnels de maison et de services, avec pour objectif un
                accès durable à un emploi digne.
              </p>

              {/* Glassmorphism info box */}
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 mb-8 max-w-2xl border border-white/20" data-id="element-1040" data-mp="2a8kc4ynfkak7">
                <h3 className="font-bold text-lg mb-2" data-id="element-1041" data-mp="2f7dim74av1av">
                  Chez ADCC, l'accompagnement pré et post insertion.
                </h3>
                <p className="text-gray-200 text-sm leading-relaxed" data-id="element-1042" data-mp="209uqcusey5yo">
                  Nous accompagnons les bénéficiaires jusqu'à l'insertion
                  professionnelle, en facilitant leur mise en relation avec des
                  employeurs, des familles, des structures et des partenaires,
                  dans un cadre éthique, transparent et respectueux des droits
                  humains.
                </p>
              </div>

              <div className="flex flex-wrap gap-4" data-id="element-1043" data-mp="1mipyievaehyw">
                <Button size="lg" className="text-lg px-8 py-6 bg-[#1E88E5] hover:bg-[#1976D2] gap-2" onClick={() => setActiveModal('formation')} data-id="element-1044" data-mp="1mj97v76vfy1b">
                  S'enregistrer pour une session
                  <ArrowRight className="w-5 h-5" data-id="element-1045" data-mp="1sz9nkb19jjyp" />
                </Button>
              </div>
            </div>

            {/* CTA Cards - Right Side */}
            <div className="space-y-4" data-id="element-1046" data-mp="1nzdkqf4axykq">
              {/* Devenir Membre Card */}
              <div className="bg-white rounded-xl p-6 shadow-xl" data-id="element-1047" data-mp="1j47yff6zmhw4">
                <div className="flex items-center gap-3 mb-4" data-id="element-1048" data-mp="1e20jqhurwnq3">
                  <div className="p-3 bg-[#1E88E5] rounded-full" data-id="element-1049" data-mp="1a16tvixrsyuk">
                    <UserPlus className="w-6 h-6 text-white" data-id="element-1050" data-mp="1a3jj8h5uybhn" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1051" data-mp="1595hlx0lr84j">
                    Rejoignez-nous
                  </h3>
                </div>
                <p className="text-gray-600 mb-4 text-sm" data-id="element-1052" data-mp="vcsszdjwzkgn">
                  Devenez bénévole de l'ADCC et participez à notre mission pour
                  l'insertion professionnelle digne.
                </p>
                <Link to="/benevoles" data-id="element-1053" data-mp="mbck5a8qzqej">
                  <Button className="w-full gap-2" data-id="element-1054" data-mp="mcnwhyg9e6h9">
                    <Heart className="w-4 h-4" data-id="element-1055" data-mp="2xzr48f76ngua" />
                    Devenir bénévole
                  </Button>
                </Link>
              </div>

              {/* Recruter Card */}
              <div className="bg-gradient-to-br from-[#1E88E5] to-[#1565C0] rounded-xl p-6 shadow-xl text-white" data-id="element-1056" data-mp="3705r3izsp3zw">
                <div className="flex items-center gap-3 mb-4" data-id="element-1057" data-mp="3bz8wx5nlne8x">
                  <div className="p-3 bg-white/20 rounded-full" data-id="element-1058" data-mp="3bwmoz4wsqwq6">
                    <Briefcase className="w-6 h-6 text-white" data-id="element-1059" data-mp="2ecteo1ncmckb" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-1060" data-mp="2jb3begoq1l81">Vous recrutez ?</h3>
                </div>
                <p className="text-blue-100 mb-4 text-sm" data-id="element-1061" data-mp="2o8d7ugaj2x5d">
                  Accédez à des personnels compétents et accompagnés pour vos
                  besoins.
                </p>
                <Button variant="ghost" className="w-full bg-white text-[#1E88E5] hover:bg-gray-100 hover:text-[#1E88E5] gap-2" onClick={() => setActiveModal('recruter')} data-id="element-1062" data-mp="1vliqvtcx6fwn">
                  <Users className="w-4 h-4" data-id="element-1063" data-mp="2bqectaye12x0" />
                  Recruter un personnel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats Section - Updated with real numbers */}
      <section className="py-12 bg-white -mt-8 relative z-10" data-id="element-1064" data-mp="2qogfzk1ibmdq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1065" data-mp="2qlkjm05b55re">
          <div className="text-center mb-8" data-id="element-1066" data-mp="1t1hjb9ateefu">
            <span className="inline-block bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-medium" data-id="element-1067" data-mp="1xz99nyvt9shx">
              Nos objectifs 2026
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1068" data-mp="22w9hae8b73b5">
            {[{
            icon: GraduationCap,
            label: 'Personnes à accompagner',
            value: '100',
            subtitle: 'Objectif 2026'
          }, {
            icon: Briefcase,
            label: 'Taux de placement visé',
            value: '60%',
            subtitle: 'Des bénéficiaires accompagnés'
          }, {
            icon: Heart,
            label: "D'insertion éthique",
            value: '100%',
            subtitle: 'Transparents et dignes'
          }].map((stat, index) => <Card key={index} className="bg-white shadow-lg border-t-4 border-t-[#1E88E5]" data-id="element-1069" data-mp="3elmlcrtq4qje" data-mpi={index}>
                <CardContent className="flex items-center p-6" data-id="element-1070" data-mp="34se1p7ybs2n8">
                  <div className="p-4 bg-blue-50 rounded-full mr-4" data-id="element-1071" data-mp="30rvang49b0m3">
                    <stat.icon className="w-8 h-8 text-[#1E88E5]" data-id="element-1072" data-mp="30s3opbc48byv" />
                  </div>
                  <div data-id="element-1073" data-mp="2vullm1ks4qys">
                    <p className="text-3xl font-bold text-gray-900" data-id="element-1074" data-mp="2vqxrb07ig0j0">
                      {stat.value}
                    </p>
                    <p className="text-gray-600 font-medium" data-id="element-1075" data-mp="2lu2vwq6jag8y">{stat.label}</p>
                    <p className="text-xs text-gray-400" data-id="element-1076" data-mp="2c6ywx373ojhe">{stat.subtitle}</p>
                  </div>
                </CardContent>
              </Card>)}
          </div>
          <div className="text-center mt-6" data-id="element-1077" data-mp="2h3z118ke3c5v">
            <p className="text-sm text-gray-500" data-id="element-1078" data-mp="236sp9xy32dq5">
              🌱 Notre premier forum a réuni ~10 personnels de maison pour
              cartographier les besoins
            </p>
          </div>
        </div>
      </section>

      {/* Value Chain Section - Updated with 5 pillars */}
      <section className="py-20 bg-gray-50" data-id="element-1079" data-mp="2zw9yuo06sjx4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1080" data-mp="2zw1mcx7z7gmg">
          <div className="text-center mb-12" data-id="element-1081" data-mp="2m5oc2o7m3pty">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1082" data-mp="2m4wkrpl1se9h">
              DE LA MONTÉE EN COMPÉTENCES À L'EMPLOI : UN PARCOURS COMPLET
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1083" data-mp="3durl0hag1ocx">
              Notre chaîne de valeur
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1084" data-mp="1782byrz8ebl9">
              L'ADCC développe une approche complète pour garantir un impact
              réel, mesurable et durable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12" data-id="element-1085" data-mp="1gyetscfnvqnp">
            {[{
            step: '01',
            title: 'Développer',
            desc: 'Compétences techniques et humaines',
            icon: GraduationCap,
            color: 'bg-blue-50'
          }, {
            step: '02',
            title: 'Tester',
            desc: 'Validation et professionnalisation',
            icon: Award,
            color: 'bg-green-50'
          }, {
            step: '03',
            title: 'Insérer',
            desc: 'Mise en relation avec employeurs',
            icon: Briefcase,
            color: 'bg-purple-50'
          }, {
            step: '04',
            title: 'Suivre',
            desc: 'Accompagnement post-placement',
            icon: TrendingUp,
            color: 'bg-amber-50'
          }, {
            step: '05',
            title: 'Recycler',
            desc: 'Mise à jour des compétences',
            icon: Recycle,
            color: 'bg-teal-50'
          }].map((item, index) => <div key={index} className="relative" data-id="element-1086" data-mp="x341cebgc4hz" data-mpi={index}>
                <Card className="h-full bg-white hover:shadow-lg transition-shadow" data-id="element-1087" data-mp="123hxoz3oxq06">
                  <CardContent className="p-5" data-id="element-1088" data-mp="16uhvs7ojqe49">
                    <div className="flex items-center justify-between mb-3" data-id="element-1089" data-mp="16vt9az7sn08k">
                      <span className="text-3xl font-bold text-gray-200" data-id="element-1090" data-mp="1avbjazy2tqwm">
                        {item.step}
                      </span>
                      <div className={`p-2 ${item.color} rounded-lg`} data-id="element-1091" data-mp="1ftv590co72oz">
                        <item.icon className="w-5 h-5 text-[#1E88E5]" data-id="element-1092" data-mp="1kvapwraxr76u" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1093" data-mp="1uwd30bbuad2o">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-xs" data-id="element-1094" data-mp="1l00c1l7oidbm">{item.desc}</p>
                  </CardContent>
                </Card>
                {index < 4 && <div className="hidden lg:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10" data-id="element-1095" data-mp="1h11becszd2pz">
                    <ArrowRight className="w-4 h-4 text-[#1E88E5]" data-id="element-1096" data-mp="2dr1ubv6o4ozd" />
                  </div>}
              </div>)}
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100" data-id="element-1097" data-mp="23y33s7rm2od4">
            <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1098" data-mp="1zwre628qm34x">
              Notre engagement éthique
            </h3>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1099" data-mp="2wh9rzxrlmn86">
              Nous œuvrons pour une insertion responsable, fondée sur :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1100" data-mp="prpzok93zvoo">
              {['La dignité du travail', 'Le respect des personnes', 'La transparence des conditions', 'La valorisation des compétences acquises'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-1101" data-mp="kzy5lzqqv7ts" data-mpi={index}>
                  <CheckCircle2 className="w-5 h-5 text-[#1E88E5] flex-shrink-0" data-id="element-1102" data-mp="70yjayqpf50s" />
                  <span className="text-gray-700" data-id="element-1103" data-mp="73ba81efwppz">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section - Fixed overlap issue */}
      <section className="py-20 bg-white" data-id="element-1104" data-mp="21vpybk36n8n">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1105" data-mp="22x2ylknxjt0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1106" data-mp="1rh1vh0k1m3ad">
            <div className="relative" data-id="element-1107" data-mp="1rez2c7nlfu1f">
              <div className="bg-gradient-to-br from-[#1E88E5]/10 via-[#2C3E50]/5 to-[#1E88E5]/10 rounded-2xl p-8 flex items-center justify-center min-h-[400px]" data-id="element-1108" data-mp="1wg4x0b0tdrdl">
                <div className="text-center" data-id="element-1109" data-mp="3byukc22frp6s">
                  <img src="https://cdn.magicpatterns.com/uploads/igFPFWe6QurLv1Fq8d9zkK/logo_sans_texte.png" alt="ADCC - Accompagner la croissance" className="w-48 h-48 mx-auto mb-6" data-id="element-1110" data-mp="3c05ldu4hyiu7" />
                  <div className="space-y-4" data-id="element-1111" data-mp="3irbhszl7ugv">
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1112" data-mp="3hg83ua20oqa">
                      <GraduationCap className="w-6 h-6 text-[#1E88E5]" data-id="element-1113" data-mp="3vmk01ax3lyzn" />
                      <span className="font-medium text-gray-700" data-id="element-1114" data-mp="dfw0e0kti41d">
                        Accompagnement à la professionalisation
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1115" data-mp="2f7scpcet7hx7">
                      <Briefcase className="w-6 h-6 text-[#1E88E5]" data-id="element-1116" data-mp="2aasefno7bjgn" />
                      <span className="font-medium text-gray-700" data-id="element-1117" data-mp="1oguujr8xp9ln">
                        Insertion accompagnée
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1118" data-mp="1td1r4tffx8qx">
                      <RefreshCw className="w-6 h-6 text-[#1E88E5]" data-id="element-1119" data-mp="2357s2sno70i9" />
                      <span className="font-medium text-gray-700" data-id="element-1120" data-mp="234fvpfm90yqx">
                        Recyclage des compétences
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-[#1E88E5] text-white p-6 rounded-lg shadow-lg" data-id="element-1121" data-mp="1ffwol3exqs0f">
                <p className="font-bold text-lg mb-2" data-id="element-1122" data-mp="1fhzlm7ei9len">
                  "Des compétences avérées, des emplois dignes."
                </p>
                <p className="text-sm text-blue-100" data-id="element-1123" data-mp="2qvleo3nhk75g">
                  L'impact se mesure aussi à l'accès à l'emploi.
                </p>
              </div>
            </div>

            <div data-id="element-1124" data-mp="k69vq81wu2se">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1125" data-mp="k6t3wx1rdct8">
                NOTRE IMPACT
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6" data-id="element-1126" data-mp="f9jhzrdkvja5">
                Renforcer des compétences ne suffit pas. Insérer durablement est
                notre priorité.
              </h2>
              <div className="space-y-6 text-lg text-gray-600" data-id="element-1127" data-mp="ikc4o44n8u9">
                <p data-id="element-1128" data-mp="k3seeps9jw1">
                  L'ADCC s'engage à promouvoir la dignité humaine par le
                  renforcement des compétences et leur transformation en
                  opportunités d'emploi concrètes. Nous croyons que chaque
                  individu possède un potentiel unique qui ne demande qu'à être
                  révélé et valorisé sur le marché du travail.
                </p>
              </div>

              <div className="space-y-4 mt-8 mb-8" data-id="element-1129" data-mp="1ltj2a6jimlaf">
                {['Développement de compétences métier', 'Insertion en emploi accompagné', 'Suivi post-insertion et médiation', 'Recyclage des personnels déjà en service', 'Développement de partenariats employeurs'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-1130" data-mp="1yypduosc07dh" data-mpi={index}>
                    <CheckCircle2 className="w-5 h-5 text-[#1E88E5] flex-shrink-0" data-id="element-1131" data-mp="1u1fnn6x4brol" />
                    <span className="text-gray-700" data-id="element-1132" data-mp="1q157x2jixmwj">{item}</span>
                  </div>)}
              </div>

              <Link to="/impact" data-id="element-1133" data-mp="1l3dhkcyj28ug">
                <Button className="bg-[#2C3E50] hover:bg-[#1a252f] text-white gap-2" data-id="element-1134" data-mp="1g1xzmtrnciga">
                  Découvrir notre approche complète
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-[#2C3E50] text-white" data-id="element-1135" data-mp="3adut0s9nfydz">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1136" data-mp="35ifgq4raiajo">
          <div className="text-center mb-10" data-id="element-1137" data-mp="3dyej6cafrlbh">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1138" data-mp="3dxmtfi3isht4">
              Découvrez l'ADCC en vidéo
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4" data-id="element-1139" data-mp="3mqq27d3806hf">
              Notre mission en images
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto" data-id="element-1140" data-mp="3rohxmh32qaqd">
              Plongez au cœur de notre engagement pour la dignité et l'insertion
              professionnelle des personnels de maison au Bénin.
            </p>
          </div>

          <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 aspect-video" data-id="element-1141" data-mp="2z2p2mto0s0e5">
            <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/tAvL6v7B_po" title="Vidéo de présentation de l'ADCC" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen loading="lazy" data-id="element-1142" data-mp="38ys2ttzb9oyg"></iframe>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-gray-50" data-id="element-1143" data-mp="3pt2rbl1gezj6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1144" data-mp="8jecah0iavvg">
          <div className="text-center mb-12" data-id="element-1145" data-mp="8gigozc4sja6">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1146" data-mp="3ktfysodkwca">
              Ils nous font confiance
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1147" data-mp="3bx8q7o6x9egm">
              Nos Partenaires
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1148" data-mp="3lpwpqqpadwj1">
              L'ADCC s'associe à des acteurs engagés pour maximiser l'impact de
              ses actions en faveur de l'insertion professionnelle digne.
            </p>
          </div>

          {/* Premium Partner */}
          <div className="max-w-2xl mx-auto" data-id="element-1149" data-mp="2oa8yl0dfxequ">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden" data-id="element-1150" data-mp="2o9hfuq4xzbhz">
              {/* Premium badge */}
              <div className="absolute top-4 right-4" data-id="element-1151" data-mp="18iewr2yvzugs">
                <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-1152" data-mp="18jzqiqddn9dl">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-1153" data-mp="ytn8b4t1bsam">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1154" data-mp="1rhas4hmheew7" />
                  </svg>
                  Partenaire Premium
                </span>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-1155" data-mp="61v93k50qst7">
                <div className="flex-shrink-0" data-id="element-1156" data-mp="mcnlzqp2yzku">
                  <img src="https://cdn.magicpatterns.com/uploads/c5DXfmbRapYgHk79paP5RZ/Logo_Trace_Academia_(2).png" alt="TRACE ACADEMIA - Partenaire Premium" className="h-24 w-auto" data-id="element-1157" data-mp="hk48suub26gk" />
                </div>
                <div className="text-center md:text-left" data-id="element-1158" data-mp="159yfsvry10sw">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1159" data-mp="159f9kcbno0up">
                    TRACE ACADEMIA
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1160" data-mp="vc0tidtn9e2x">
                    Plateforme de formation en ligne leader en Afrique, TRACE
                    ACADEMIA rejoint l'ADCC pour renforcer les compétences des
                    personnels de maison à travers des contenus éducatifs de
                    qualité.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-1161" data-mp="3bzlfhubq47a1">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1162" data-mp="1rjf26p7otqb9">
                      Formation digitale
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1163" data-mp="20j25xhnw387h">
                      Certification
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1164" data-mp="2a9wooc1xfpo6">
                      Contenus exclusifs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Future partners placeholder */}
          <div className="mt-12 text-center" data-id="element-1165" data-mp="2o6saz676vdm6">
            <p className="text-gray-500 text-sm mb-4" data-id="element-1166" data-mp="owk7cvuzbl5q">
              Vous souhaitez devenir partenaire de l'ADCC ?
            </p>
            <Link to="/partner" data-id="element-1167" data-mp="tvdk2m2fz84u">
              <Button variant="secondary" className="gap-2" data-id="element-1168" data-mp="1x5rfda260vp7">
                <Handshake className="w-4 h-4" data-id="element-1169" data-mp="1x6axaqrtfk38" />
                Proposer un partenariat
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2980B9] text-white" data-id="element-1170" data-mp="2680phytiun22">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1171" data-mp="216uvzys1evrh">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1172" data-mp="2g4n9kle0xa31">
            Soutenez l'insertion professionnelle durable
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1173" data-mp="2b6bvtzklyffj">
            Votre soutien permet non seulement de développer les compétences,
            mais aussi de financer des dispositifs d'accompagnement de
            l'insertion des bénéficiaires vers un emploi durable, et de
            structurer ainsi un modèle d'aide à l'emploi éthique et responsable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1174" data-mp="2tw1pgjcmwv5v">
            <Link to="/donate" data-id="element-1175" data-mp="2ttxghmzvp5yq">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 hover:text-white font-medium gap-2 w-full sm:w-auto" data-id="element-1176" data-mp="3dhad8s8ycw7s">
                <Heart className="w-5 h-5" data-id="element-1177" data-mp="33im82u22gbli" />
                Faire un don
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 hover:text-white w-full sm:w-auto gap-2" onClick={() => setActiveModal('recruter')} data-id="element-1178" data-mp="38hfkej5m9wk3">
              <Briefcase className="w-5 h-5" data-id="element-1179" data-mp="2ktektzqf1qgt" />
              Recruter un personnel
            </Button>
          </div>
        </div>
      </section>

      {/* Modal */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-id="element-1180" data-mp="2bsgg1f85lex6">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8 relative max-h-[90vh] overflow-y-auto" data-id="element-1181" data-mp="2fubdu9qvla67">
            <button onClick={closeModal} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors z-10" data-id="element-1182" data-mp="2w89d3ns6m4pv">
              <X className="w-6 h-6" data-id="element-1183" data-mp="2ma3fhiderkqt" />
            </button>

            <div className="p-8" data-id="element-1184" data-mp="2hck18igpn7yv">
              <div className="text-center mb-8" data-id="element-1185" data-mp="2hba35t9uxzs8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E88E5] rounded-full mb-4" data-id="element-1186" data-mp="2heo6b3qcid0q">
                  {activeModal === 'membre' ? <UserPlus className="w-8 h-8 text-white" data-id="element-1187" data-mp="28if6hnyghce2" /> : activeModal === 'recruter' ? <Briefcase className="w-8 h-8 text-white" data-id="element-1188" data-mp="23isrb8z2hm2m" /> : <GraduationCap className="w-8 h-8 text-white" data-id="element-1189" data-mp="1ynn7qgt50jhp" />}
                </div>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1190" data-mp="1totrigmguedu">
                  {modalConfigs[activeModal].title}
                </h2>
                <p className="text-gray-600 mt-2" data-id="element-1191" data-mp="53mlum8b882x">
                  {modalConfigs[activeModal].subtitle}
                </p>
              </div>

              {isSuccess ? <div className="text-center py-12" data-id="element-1192" data-mp="96j6sjlboga1">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1193" data-mp="e52xexav2g8q">
                    <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1194" data-mp="j233373stgzb" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1195" data-mp="3hmbcj9e7f1zr">
                    {activeModal === 'membre' ? 'Bienvenue dans la famille ADCC !' : activeModal === 'recruter' ? 'Demande envoyée !' : 'Inscription enregistrée !'}
                  </h3>
                  <p className="text-gray-600 mb-8" data-id="element-1196" data-mp="2yuikpiavfmtm">
                    {modalConfigs[activeModal].successMessage}
                  </p>
                  <Button onClick={closeModal} data-id="element-1197" data-mp="33riofmk90dhk">Fermer</Button>
                </div> : <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-1198" data-mp="2djpjb9yie0zd">
                  {/* Formulaire Membre */}
                  {activeModal === 'membre' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1199" data-mp="28jti1ih74nni">
                      <div className="space-y-2" data-id="element-1200" data-mp="28lxtqmlc6qyc">
                        <label className={labelClass} data-id="element-1201" data-mp="1urn84e4mzcwi">Nom complet *</label>
                        <input type="text" required className={inputClass} name="nom" placeholder="Votre nom" data-id="element-1202" data-mp="1zjwyfv9nyezb" />
                      </div>
                      <div className="space-y-2" data-id="element-1203" data-mp="1bu2i33q4y8ab">
                        <label className={labelClass} data-id="element-1204" data-mp="1bum4awmtjiu5">Prénom *</label>
                        <input type="text" required className={inputClass} name="prenom" placeholder="Votre prénom" data-id="element-1205" data-mp="1grvpg036d8c6" />
                      </div>
                      <div className="space-y-2" data-id="element-1206" data-mp="168v08ooxw4uq">
                        <label className={labelClass} data-id="element-1207" data-mp="165gy9hk6txnt">Téléphone *</label>
                        <input type="tel" required className={inputClass} name="telephone" placeholder="+229..." data-id="element-1208" data-mp="124nbwsme8qx1" />
                      </div>
                      <div className="space-y-2" data-id="element-1209" data-mp="1pv9j6hokq75z">
                        <label className={labelClass} data-id="element-1210" data-mp="1kwya4963s0ot">Email *</label>
                        <input type="email" required className={inputClass} name="email" placeholder="votre@email.com" data-id="element-1211" data-mp="1kysrxem2vcoo" />
                      </div>
                      <div className="space-y-2" data-id="element-1212" data-mp="rhvlrhbjqg1n">
                        <label className={labelClass} data-id="element-1213" data-mp="wk4ecckibg38">Ville / Commune *</label>
                        <input type="text" required className={inputClass} name="ville" placeholder="Ex: Cotonou" data-id="element-1214" data-mp="10k4sdw3p1p9r" />
                      </div>
                      <div className="space-y-2" data-id="element-1215" data-mp="1f9asdmacvjip">
                        <label className={labelClass} data-id="element-1216" data-mp="1f7pubmou1ig3">Profession</label>
                        <input type="text" className={inputClass} name="profession" placeholder="Votre profession actuelle" data-id="element-1217" data-mp="1k9p0f4byitgq" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1218" data-mp="3q12g941teuf3">
                        <label className={labelClass} data-id="element-1219" data-mp="3q3f6e4hq8b38">Type de membre *</label>
                        <select required className={selectClass} name="typeMembre" data-id="element-1220" data-mp="3ije10zbpocnd">
                          <option value="" data-id="element-1221" data-mp="38st55jnf9m85">Sélectionnez...</option>
                          <option value="actif" data-id="element-1222" data-mp="34r7tu2qjdg02">Membre actif</option>
                          <option value="sympathisant" data-id="element-1223" data-mp="34ov8dflkkni9">Sympathisant</option>
                          <option value="donateur" data-id="element-1224" data-mp="5arqaqjjfb8h">Donateur</option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1225" data-mp="3sg8ydew90702">
                        <label className={labelClass} data-id="element-1226" data-mp="3sepjnsq8q40e">
                          Contribution souhaitée
                        </label>
                        <textarea className={inputClass} rows={3} name="contribution" placeholder="Comment souhaitez-vous aider ? (Temps, réseau, dons...)" data-id="element-1227" data-mp="2h13ui7kds2ev"></textarea>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1228" data-mp="8kav0wsibr82">
                        <label className={labelClass} data-id="element-1229" data-mp="8lvsau67ho9m">Motivations *</label>
                        <textarea required className={inputClass} rows={3} name="motivations" placeholder="Pourquoi souhaitez-vous rejoindre l'ADCC ?" data-id="element-1230" data-mp="3qzv8djyvftsd"></textarea>
                      </div>
                    </div>}

                  {/* Formulaire Recruter */}
                  {activeModal === 'recruter' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1231" data-mp="ignmrp4hrv06">
                      <div className="space-y-2" data-id="element-1232" data-mp="2y93bsldw934i">
                        <label className={labelClass} data-id="element-1233" data-mp="2ybploro9bupw">Nom complet *</label>
                        <input type="text" required className={inputClass} name="nom" placeholder="Votre nom" data-id="element-1234" data-mp="33a0ur06qa172" />
                      </div>
                      <div className="space-y-2" data-id="element-1235" data-mp="34p38v03skk68">
                        <label className={labelClass} data-id="element-1236" data-mp="2zqrzsrlbmdp2">Prénom *</label>
                        <input type="text" required className={inputClass} name="prenom" placeholder="Votre prénom" data-id="element-1237" data-mp="2zofimgne07d2" />
                      </div>
                      <div className="space-y-2" data-id="element-1238" data-mp="2lsu0mtfkmnu5">
                        <label className={labelClass} data-id="element-1239" data-mp="2gzr0g2gagg4z">Téléphone *</label>
                        <input type="tel" required className={inputClass} name="telephone" placeholder="+229..." data-id="element-1240" data-mp="1ta6inmvvfoxa" />
                      </div>
                      <div className="space-y-2" data-id="element-1241" data-mp="3tcpjt1fl84ni">
                        <label className={labelClass} data-id="element-1242" data-mp="3tbeeuyaeoqut">Email *</label>
                        <input type="email" required className={inputClass} name="email" placeholder="votre@email.com" data-id="element-1243" data-mp="1xi8y3jm7gff" />
                      </div>
                      <div className="space-y-2" data-id="element-1244" data-mp="3ad1vkjps60li">
                        <label className={labelClass} data-id="element-1245" data-mp="3afxuobdd6vbj">Ville / Commune *</label>
                        <input type="text" required className={inputClass} name="ville" placeholder="Ex: Cotonou" data-id="element-1246" data-mp="3edvk9fb1rjfg" />
                      </div>
                      <div className="space-y-2" data-id="element-1247" data-mp="ymprr3sunbfn">
                        <label className={labelClass} data-id="element-1248" data-mp="18ry2f5s3qm8o">Quartier *</label>
                        <input type="text" required className={inputClass} name="quartier" placeholder="Ex: Haie Vive" data-id="element-1249" data-mp="18ubud5rhjic0" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1250" data-mp="uz03j9ggac0d">
                        <label className={labelClass} data-id="element-1251" data-mp="q2j2xfs2e56e">
                          Type de personnel recherché *
                        </label>
                        <select required className={selectClass} name="typePersonnel" data-id="element-1252" data-mp="g3k4sr4xdrxn">
                          <option value="" data-id="element-1253" data-mp="c4bb1m9vxu23">Sélectionnez...</option>
                          <option value="maison" data-id="element-1254" data-mp="2ah1jdoncregj">Personnel de maison</option>
                          <option value="hotellerie" data-id="element-1255" data-mp="26fpzlzr5tng9">
                            Hôtellerie / Restauration
                          </option>
                          <option value="aide" data-id="element-1256" data-mp="2pquafbjlw378">Aide à la personne</option>
                          <option value="autre" data-id="element-1257" data-mp="2tp1ili007w6t">Autre</option>
                        </select>
                      </div>
                      <div className="space-y-2" data-id="element-1258" data-mp="2fyob1gr0yji0">
                        <label className={labelClass} data-id="element-1259" data-mp="2kx7w7exsnr9b">
                          Type de contrat souhaité *
                        </label>
                        <select required className={selectClass} name="typeContrat" data-id="element-1260" data-mp="25zfh2nw5t4vn">
                          <option value="" data-id="element-1261" data-mp="1x1n0lgepbr6o">Sélectionnez...</option>
                          <option value="cdi" data-id="element-1262" data-mp="21ydga2mlgkrk">CDI</option>
                          <option value="cdd" data-id="element-1263" data-mp="2219hbztqnpk8">CDD</option>
                          <option value="ponctuel" data-id="element-1264" data-mp="3rf4pb6queu3a">Mission ponctuelle</option>
                        </select>
                      </div>
                      <div className="space-y-2" data-id="element-1265" data-mp="5pjoc4ggvkr">
                        <label className={labelClass} data-id="element-1266" data-mp="aaxhvemiudww">
                          Date de début souhaitée *
                        </label>
                        <input type="date" required className={inputClass} name="dateDebut" data-id="element-1267" data-mp="5eguimn5c3kv" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1268" data-mp="t1ozf3gnxamd">
                        <label className={labelClass} data-id="element-1269" data-mp="t41jbm5zdv22">
                          Description du besoin *
                        </label>
                        <textarea required className={inputClass} rows={4} name="description" placeholder="Détaillez les tâches, horaires, et profil recherché..." data-id="element-1270" data-mp="1236eqjm8x8a0"></textarea>
                      </div>
                    </div>}

                  {/* Formulaire Formation */}
                  {activeModal === 'formation' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1271" data-mp="1a3fkn3yxjn04">
                      <div className="space-y-2" data-id="element-1272" data-mp="1o25hqjlr1moh">
                        <label className={labelClass} data-id="element-1273" data-mp="1o3yn7vkmm0uw">Nom *</label>
                        <input type="text" required className={inputClass} name="nom" placeholder="Votre nom" data-id="element-1274" data-mp="1k0k774wotkhg" />
                      </div>
                      <div className="space-y-2" data-id="element-1275" data-mp="27qwbm5sq4z3a">
                        <label className={labelClass} data-id="element-1276" data-mp="27plcwke4yhj1">Prénom *</label>
                        <input type="text" required className={inputClass} name="prenom" placeholder="Votre prénom" data-id="element-1277" data-mp="22smgtwaww0i7" />
                      </div>
                      <div className="space-y-2" data-id="element-1278" data-mp="2vf2zseeg8m9z">
                        <label className={labelClass} data-id="element-1279" data-mp="30cupr2vj9ybj">Âge *</label>
                        <input type="number" required min="16" max="65" className={inputClass} name="age" placeholder="Votre âge" data-id="element-1280" data-mp="30c368qf7nr1m" />
                      </div>
                      <div className="space-y-2" data-id="element-1281" data-mp="2hea07f9aj100">
                        <label className={labelClass} data-id="element-1282" data-mp="2hg380xvn3r9l">Ville *</label>
                        <input type="text" required className={inputClass} name="ville" placeholder="Ex: Cotonou" data-id="element-1283" data-mp="22qfc6c689kt9" />
                      </div>
                      <div className="space-y-2" data-id="element-1284" data-mp="1ym7jy50k9mqa">
                        <label className={labelClass} data-id="element-1285" data-mp="47ainve4jjq9">Quartier *</label>
                        <input type="text" required className={inputClass} name="quartier" placeholder="Ex: Haie Vive" data-id="element-1286" data-mp="49dfay9s8b3y" />
                      </div>
                      <div className="space-y-2" data-id="element-1287" data-mp="3mj6yl8bxafqk">
                        <label className={labelClass} data-id="element-1288" data-mp="n6enerymurc4">Téléphone *</label>
                        <input type="tel" required className={inputClass} name="telephone" placeholder="+229..." data-id="element-1289" data-mp="i7bg10v6k90z" />
                      </div>
                      <div className="space-y-2" data-id="element-1290" data-mp="2yvpdfvn5yfwk">
                        <label className={labelClass} data-id="element-1291" data-mp="2ywh0uj6lx7br">WhatsApp</label>
                        <input type="tel" className={inputClass} name="whatsapp" placeholder="+229... (si différent)" data-id="element-1292" data-mp="2twcrdd449cv1" />
                      </div>
                      <div className="space-y-2" data-id="element-1293" data-mp="1if9v76fot184">
                        <label className={labelClass} data-id="element-1294" data-mp="1iegmw112y3nv">Email</label>
                        <input type="email" className={inputClass} name="email" placeholder="votre@email.com" data-id="element-1295" data-mp="1n6i6k80kuwnx" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1296" data-mp="1x5z9wiscwygq">
                        <label className={labelClass} data-id="element-1297" data-mp="223qz351ma6h8">
                          Type de session souhaitée *
                        </label>
                        <select required className={selectClass} name="typeFormation" data-id="element-1298" data-mp="7p7wz3kzm4zq">
                          <option value="" data-id="element-1299" data-mp="bsmnwpags5ry">Sélectionnez...</option>
                          <option value="aide_menager" data-id="element-1300" data-mp="3ckg1c5ax416b">Aide-ménager(e)</option>
                          <option value="agent_entretien" data-id="element-1301" data-mp="38je1bm96fl3a">
                            Agent(e) d'entretien
                          </option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1302" data-mp="3hm5hfzczz519">
                        <label className={labelClass} data-id="element-1303" data-mp="2tv9sd9zxenxp">
                          Quelle est votre motivation ? *
                        </label>
                        <textarea required className={inputClass} rows={3} name="motivations" placeholder="Décrivez brièvement ce qui vous motive à suivre cette session..." data-id="element-1304" data-mp="2oxrdl2z89ehs"></textarea>
                      </div>
                      {/* Charte d'engagement du bénéficiaire */}
                      <div className="md:col-span-2 space-y-4 bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-1305" data-mp="2644cwvxwdxnx">
                        <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-1306" data-mp="qemnyxa8vxk2">
                          <Shield className="w-5 h-5 text-[#1E88E5]" data-id="element-1307" data-mp="qghbmjcwi3ru" />
                          Charte d'engagement du bénéficiaire ADCC
                        </h3>
                        <div className="h-48 overflow-y-auto text-sm text-gray-600 bg-white p-4 rounded border border-gray-200 space-y-4" data-id="element-1308" data-mp="gpctjarxi2ss">
                          <p className="font-bold text-gray-800" data-id="element-1309" data-mp="19dienymw9tt5">Préambule</p>
                          <p data-id="element-1310" data-mp="19bedhogdr8wk">
                            La présente Charte d'Engagement formalise la
                            relation de confiance et de co-responsabilité entre
                            ADCC et chaque personne bénéficiaire de ses
                            formations et accompagnements.
                          </p>
                          <p data-id="element-1311" data-mp="3l3ku1vefijvf">
                            Chez ADCC, nous croyons profondément que chaque
                            femme, quelle que soit son parcours, porte en elle
                            la capacité de progresser, de se professionnaliser
                            et de construire un emploi durable et reconnu.
                          </p>
                          <p data-id="element-1312" data-mp="1gunf2vina3b2">
                            Cette charte est un engagement mutuel : ADCC
                            s'engage à vous accompagner avec bienveillance,
                            exigence et respect. En retour, nous vous invitons à
                            vous engager pleinement dans cette aventure, pour
                            vous-même et pour celles qui viendront après vous.
                          </p>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1313" data-mp="txlrdduyl5u9">
                            1. Assiduité & Ponctualité
                          </p>
                          <p data-id="element-1314" data-mp="2oalc1le74q8e">
                            La régularité est la première marque de votre
                            engagement envers vous-même. Elle prépare votre
                            posture professionnelle et montre que vous prenez
                            votre formation au sérieux.
                          </p>
                          <p className="font-medium" data-id="element-1315" data-mp="jton7jgjdgw1">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1316" data-mp="nuqm1z6jjqxh">
                            <li data-id="element-1317" data-mp="nw1pfxw2qwo2">
                              ✦ Participer à l'ensemble des sessions de
                              formation prévues dans mon programme, sans
                              exception.
                            </li>
                            <li data-id="element-1318" data-mp="3l51gov6sll">
                              ✦ Arriver à l'heure et prévenir mon référent(e)
                              ADCC en cas d'empêchement, au plus tôt.
                            </li>
                            <li data-id="element-1319" data-mp="1bj9tkchrnzoi">
                              ✦ Justifier toute absence et, dans la mesure du
                              possible, rattraper les contenus manqués.
                            </li>
                            <li data-id="element-1320" data-mp="s6kfri9fxy1l">
                              ✦ Respecter les horaires et les délais fixés pour
                              les travaux et exercices demandés.
                            </li>
                            <li data-id="element-1321" data-mp="110qoeaofyzhy">
                              ✦ Informer ADCC de tout changement de situation
                              susceptible d'affecter ma participation.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1322" data-mp="15yihv94qito9">
                            2. Formation Théorique & Pratique — Engagement à 100
                            %
                          </p>
                          <p data-id="element-1323" data-mp="1fwy879vxtywp">
                            La formation ADCC combine modules théoriques et
                            mises en pratique concrètes. Ces deux dimensions
                            sont indissociables : l'une sans l'autre ne permet
                            pas d'atteindre la professionnalisation visée.
                          </p>
                          <p className="font-medium" data-id="element-1324" data-mp="3bkon44hfiekh">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1325" data-mp="3bmrb6iz0txmk">
                            <li data-id="element-1326" data-mp="2xt0ck5mtvy5m">
                              ✦ Suivre l'intégralité des modules de formation
                              théorique inscrits à mon programme, sans
                              exception.
                            </li>
                            <li data-id="element-1327" data-mp="2su708gja2d71">
                              ✦ Participer activement à toutes les séances
                              pratiques, ateliers et mises en situation prévus.
                            </li>
                            <li data-id="element-1328" data-mp="2jsz341shziej">
                              ✦ Préparer les séances en amont lorsque cela est
                              demandé et restituer les exercices dans les
                              délais.
                            </li>
                            <li data-id="element-1329" data-mp="i01deeu0p5xt">
                              ✦ M'investir avec sérieux et curiosité dans chaque
                              apprentissage, même lorsque la matière est
                              difficile.
                            </li>
                            <li data-id="element-1330" data-mp="1trfyalr9s3s8">
                              ✦ Solliciter l'aide de mon référent(e) ADCC dès
                              que je rencontre un obstacle, sans attendre.
                            </li>
                            <li data-id="element-1331" data-mp="22k9mj8op2di8">
                              ✦ Travailler activement à ma confiance en moi : ma
                              réussite commence dans ma propre tête.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1332" data-mp="2hibudv2rdl5a">
                            3. Stage de Certification
                          </p>
                          <p data-id="element-1333" data-mp="i6r9ho49rh2p">
                            Le stage de certification est une étape clé : il
                            valide vos acquis en situation réelle et constitue
                            le tremplin vers votre insertion professionnelle
                            durable.
                          </p>
                          <p className="font-medium" data-id="element-1334" data-mp="2rlh5alb23oge">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1335" data-mp="2rjmmbcjci6ey">
                            <li data-id="element-1336" data-mp="2mnxeklx69j7k">
                              ✦ Effectuer l'intégralité du stage de
                              certification dans les conditions et délais fixés
                              par ADCC.
                            </li>
                            <li data-id="element-1337" data-mp="3abyjlkv56hi8">
                              ✦ Adopter en stage une posture professionnelle
                              exemplaire : ponctualité, sérieux, respect,
                              initiative.
                            </li>
                            <li data-id="element-1338" data-mp="1yycwe58uepzc">
                              ✦ Rendre compte régulièrement de mon expérience à
                              mon référent(e) ADCC tout au long du stage.
                            </li>
                            <li data-id="element-1339" data-mp="1vdb4xa6jdyn2">
                              ✦ Remettre tous les documents requis (rapport,
                              évaluations, feuilles de présence) dans les délais
                              impartis.
                            </li>
                            <li data-id="element-1340" data-mp="1rc7s6wjsciq2">
                              ✦ Aborder cette étape comme une opportunité de
                              démontrer mes compétences et de créer des liens
                              professionnels durables.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1341" data-mp="13pj41t46nu10">
                            4. Respect & Posture Professionnelle
                          </p>
                          <p data-id="element-1342" data-mp="tpievqjmujow">
                            Se professionnaliser, c'est adopter une posture
                            respectueuse envers les autres et envers soi-même.
                            C'est une compétence clé, aussi importante que les
                            savoir-faire techniques.
                          </p>
                          <p className="font-medium" data-id="element-1343" data-mp="a633zeoitz3f">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1344" data-mp="p20tie3xw56o">
                            <li data-id="element-1345" data-mp="k1mvzpzysdmw">
                              ✦ Traiter avec respect et bienveillance les
                              formatrices, référentes et autres participant(e)s.
                            </li>
                            <li data-id="element-1346" data-mp="t1t9hbvnxdm2">
                              ✦ Adopter un comportement professionnel lors des
                              formations, ateliers et mises en situation.
                            </li>
                            <li data-id="element-1347" data-mp="12t5ylvq2tpoe">
                              ✦ Contribuer positivement à la dynamique du groupe
                              et encourager les autres dans leur parcours.
                            </li>
                            <li data-id="element-1348" data-mp="1coqy9n7oaduz">
                              ✦ Exprimer mes désaccords ou difficultés par la
                              parole, dans un cadre constructif et bienveillant.
                            </li>
                            <li data-id="element-1349" data-mp="3fzm4ggm6resk">
                              ✦ Respecter les locaux, le matériel et les
                              ressources mis à ma disposition.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1350" data-mp="2nbq6l8kvrgu7">
                            5. Insertion Professionnelle & Suivi du Contrat de
                            Travail
                          </p>
                          <p data-id="element-1351" data-mp="2x7b712aaw91u">
                            L'insertion dans un emploi durable est l'objectif
                            central de l'accompagnement ADCC. Pour garantir les
                            meilleures conditions d'entrée et de maintien dans
                            l'emploi, je confie à ADCC un rôle actif à mes
                            côtés.
                          </p>
                          <p className="font-medium" data-id="element-1352" data-mp="211hsifentdqu">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1353" data-mp="1x1qx1ye6u9fz">
                            <li data-id="element-1354" data-mp="1s9pp2oo1z4vr">
                              ✦ Autoriser ADCC à m'accompagner dans la rédaction
                              et la négociation des clauses de mon contrat de
                              travail avec l'employeur, afin de protéger mes
                              droits et de sécuriser mon insertion.
                            </li>
                            <li data-id="element-1355" data-mp="1det55e6vst2u">
                              ✦ Participer aux rencontres tripartites
                              (bénéficiaire — employeur — ADCC) prévues pour la
                              mise en place et le suivi du contrat.
                            </li>
                            <li data-id="element-1356" data-mp="33ocz160qd7c7">
                              ✦ Donner mon accord explicite à ADCC pour
                              contacter mon employeur dans le cadre du suivi
                              trimestriel, afin d'évaluer mon intégration et de
                              prévenir toute difficulté.
                            </li>
                            <li data-id="element-1357" data-mp="38ui1xfsmbxtk">
                              ✦ Participer aux évaluations régulières de ma
                              progression organisées par ADCC en cours de
                              contrat.
                            </li>
                            <li data-id="element-1358" data-mp="5b59xw9ef2q4">
                              ✦ Informer sans délai mon référent(e) ADCC de
                              toute difficulté rencontrée dans mon emploi
                              (relations, conditions de travail, respect du
                              contrat).
                            </li>
                            <li data-id="element-1359" data-mp="2g5kk1dsbk31c">
                              ✦ Maintenir une communication ouverte et honnête
                              avec ADCC tout au long de mon contrat, dans une
                              logique de co-construction de mon parcours
                              professionnel.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1360" data-mp="28nwa9okzidh0">
                            6. Épargne Personnelle & Fonds d'Avenir
                          </p>
                          <p data-id="element-1361" data-mp="2hnrknr6pu4fz">
                            ADCC accompagne chaque bénéficiaire dans la
                            construction de sa stabilité financière. Au-delà de
                            l'emploi, il s'agit de préparer l'avenir avec
                            méthode et confiance. Le Fonds d'Épargne Personnelle
                            est un outil concret pour y parvenir.
                          </p>
                          <p className="font-medium mt-2" data-id="element-1362" data-mp="1yu5mklwom72d">
                            Mon Fonds d'Épargne Personnelle ADCC
                          </p>
                          <p data-id="element-1363" data-mp="1669ujui253bx">
                            Dès l'entrée en emploi, je m'engage à verser chaque
                            mois 5 % de mon salaire net dans le Fonds de
                            Solidarité ADCC. Cette épargne solidaire est
                            destinée exclusivement à financer l'accompagnement
                            de futures bénéficiaires qui, comme moi, ont besoin
                            d'un soutien pour accéder à un emploi durable et
                            reconnu.
                          </p>
                          <p className="font-medium" data-id="element-1364" data-mp="18lxfhdegbb0e">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1365" data-mp="18jugi3vbm7jj">
                            <li data-id="element-1366" data-mp="1ddyomqgbolm7">
                              ✦ Verser 5 % de mon salaire net mensuel dans mon
                              Fonds d'Épargne Personnelle ADCC, dès la réception
                              de mon premier salaire.
                            </li>
                            <li data-id="element-1367" data-mp="1s94w7hv2gshn">
                              ✦ Alimenter régulièrement mon épargne tout au long
                              de mon adhésion à ADCC, sans interruption.
                            </li>
                            <li data-id="element-1368" data-mp="37s2tarxilfht">
                              ✦ Participer aux échanges proposés par ADCC sur la
                              gestion de mon budget et l'organisation de mon
                              épargne personnelle.
                            </li>
                            <li data-id="element-1369" data-mp="3vizx1hf5p6hz">
                              ✦ Informer ADCC de tout changement de situation
                              financière (perte d'emploi, modification de
                              contrat) susceptible d'affecter mes versements.
                            </li>
                            <li data-id="element-1370" data-mp="3gu3qjosr01jh">
                              ✦ Respecter la règle de retrait : je ne peux
                              retirer tout ou partie de mon épargne qu'une fois
                              par an, à l'échéance annuelle fixée avec ADCC.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1371" data-mp="23ygz2ktu8rdf">
                            7. Confidentialité & Éthique
                          </p>
                          <p data-id="element-1372" data-mp="1uwpu5i74gokm">
                            La confiance est le ciment de notre accompagnement.
                            Ce qui se partage au sein des groupes ADCC reste
                            dans ce cadre protégé. Vous en êtes la gardienne,
                            comme nous le sommes pour vous.
                          </p>
                          <p className="font-medium" data-id="element-1373" data-mp="2indi1cxfnost">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1374" data-mp="2dutsripdv586">
                            <li data-id="element-1375" data-mp="28x0mkpo2yb7w">
                              ✦ Respecter la confidentialité des informations
                              personnelles partagées par d'autres
                              participant(e)s.
                            </li>
                            <li data-id="element-1376" data-mp="31k4x1a5xzhr2">
                              ✦ Ne pas divulguer à l'extérieur les témoignages,
                              situations ou difficultés évoqués en groupe.
                            </li>
                            <li data-id="element-1377" data-mp="3dxiexr3d3wjb">
                              ✦ Utiliser les ressources et supports pédagogiques
                              fournis uniquement dans le cadre de ma formation.
                            </li>
                            <li data-id="element-1378" data-mp="3rvyl9gxbbl0x">
                              ✦ Signaler à ADCC toute situation qui me
                              semblerait contraire à l'éthique ou au respect des
                              personnes.
                            </li>
                            <li data-id="element-1379" data-mp="5hyqd5dmz2al">
                              ✦ Agir avec intégrité dans toutes mes démarches
                              liées à mon parcours de formation et d'insertion.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1380" data-mp="jevh76tr8jrh">
                            8. Droit à l'Image
                          </p>
                          <p data-id="element-1381" data-mp="2fgp3ojbonm8t">
                            Dans le cadre de mes activités au sein d'ADCC
                            (formations, ateliers, foires, événements et toute
                            autre manifestation organisée ou co-organisée par
                            ADCC), des photos et vidéos sont susceptibles d'être
                            réalisées à des fins de communication
                            institutionnelle et promotionnelle.
                          </p>
                          <p className="font-medium" data-id="element-1382" data-mp="2u9sbw03dwnmv">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1383" data-mp="386xbbh8leo95">
                            <li data-id="element-1384" data-mp="338476ff749ln">
                              ✦ Autoriser ADCC à utiliser mon image
                              (photographies et enregistrements vidéo) pris lors
                              des activités auxquelles je participe, à titre
                              gratuit et sans limitation de durée.
                            </li>
                            <li data-id="element-1385" data-mp="3kk11o65bkntl">
                              ✦ Accepter que ces contenus soient diffusés sur le
                              site internet adcc.africa, les réseaux sociaux
                              officiels d'ADCC et tout support de communication
                              numérique ou imprimé lié à ses activités.
                            </li>
                          </ul>
                          <p className="italic text-gray-500 text-xs mt-1" data-id="element-1386" data-mp="31tjjuwm0zcn0">
                            Cette autorisation peut être retirée à tout moment
                            par demande écrite adressée à ADCC, sans effet
                            rétroactif sur les contenus déjà publiés.
                          </p>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1387" data-mp="qyvy1qipxd26">
                            Les engagements d'ADCC envers vous
                          </p>
                          <p data-id="element-1388" data-mp="uyei6k2ptk3y">
                            En contrepartie de votre engagement, ADCC s'engage à
                            :
                          </p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1389" data-mp="1bjk7fkolvzo">
                            <li data-id="element-1390" data-mp="3sgi3ldzi2j4c">
                              ✦ Vous accueillir avec respect, sans jugement,
                              quelle que soit votre situation de départ.
                            </li>
                            <li data-id="element-1391" data-mp="3jhelodc2k9rf">
                              ✦ Vous proposer un accompagnement de qualité,
                              adapté à vos besoins et à votre rythme.
                            </li>
                            <li data-id="element-1392" data-mp="39rbn7xm6i3lp">
                              ✦ Vous accompagner dans la rédaction de votre
                              contrat et protéger vos droits auprès de
                              l'employeur.
                            </li>
                            <li data-id="element-1393" data-mp="2vqiot1sraodb">
                              ✦ Assurer un suivi trimestriel avec votre
                              employeur pour prévenir les difficultés et
                              consolider votre emploi.
                            </li>
                            <li data-id="element-1394" data-mp="1fcfv58ljmiaw">
                              ✦ Vous évaluer régulièrement en cours de contrat
                              et célébrer chacun de vos progrès.
                            </li>
                            <li data-id="element-1395" data-mp="rl0qh96anowh">
                              ✦ Garantir la transparence et la bonne gestion du
                              Fonds de Solidarité au bénéfice de toutes.
                            </li>
                            <li data-id="element-1396" data-mp="11iom01bq50fp">
                              ✦ Garantir la confidentialité de vos informations
                              personnelles et de votre parcours.
                            </li>
                          </ul>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer mt-4" data-id="element-1397" data-mp="8sonqlbqallt">
                          <input type="checkbox" name="charteEngagement" value="oui" required checked={charterAccepted} onChange={e => setCharterAccepted(e.target.checked)} className="mt-1 w-5 h-5 text-[#1E88E5] border-gray-300 rounded focus:ring-[#1E88E5]" data-id="element-1398" data-mp="drj65m5sa85e" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-1399" data-mp="3eycrdst7w41f">
                            J'ai lu et j'accepte la charte d'engagement du
                            bénéficiaire ADCC *
                          </span>
                        </label>
                      </div>
                    </div>}

                  <div className="pt-4 border-t border-gray-100" data-id="element-1400" data-mp="185twhdoz4mzj">
                    <Button type="submit" className="w-full" size="lg" disabled={isSubmitting || activeModal === 'formation' && !charterAccepted} data-id="element-1401" data-mp="1393f8n1fnlcj">
                      {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-1402" data-mp="2h4bred7uljmr">
                          <Loader2 className="w-5 h-5 animate-spin" data-id="element-1403" data-mp="2h66g82m8pvw4" />
                          Envoi en cours...
                        </span> : 'Envoyer la demande'}
                    </Button>
                  </div>
                </form>}
            </div>
          </div>
        </div>}
    </Layout>;
}